import type { LipDetection } from '../tracking/faceLandmarker';
import type { SemanticFaceMap } from '../semantic/semanticFaceMap';
import { combineMasks, softenMask } from './maskUtils';
import { stackBlur } from './stackBlur';

export type MakeupRefinementMode = 'local-pixel' | 'none';
export type MakeupRefinementRequestMode = MakeupRefinementMode | 'auto';
export type MakeupRefinementRuntime = 'canvas2d' | 'none';
export type MakeupRefinementPlatform = 'ios' | 'android' | 'desktop' | 'unknown';

export interface MakeupRefinementCapabilityReport {
  platform: MakeupRefinementPlatform;
  selectedMode: MakeupRefinementMode;
  runtime: MakeupRefinementRuntime;
  webgpuAvailable: boolean;
  webgl2Available: boolean;
  offscreenCanvasAvailable: boolean;
  wasmAvailable: boolean;
  nativeBridgeAvailable: boolean;
  reason: string;
}

export interface MakeupRefinementOptions {
  semanticMap?: SemanticFaceMap | null;
  mode?: MakeupRefinementRequestMode;
  textureStrength?: number;
  edgeBlendStrength?: number;
}

export interface MakeupRefinementResult {
  canvas: HTMLCanvasElement;
  mode: MakeupRefinementMode;
  runtime: MakeupRefinementRuntime;
  capabilities: MakeupRefinementCapabilityReport;
  textureStrength: number;
  edgeBlendStrength: number;
}

let cachedWebGl2Available: boolean | null = null;
let cachedPlatform: MakeupRefinementPlatform | null = null;
const capabilityCache = new Map<MakeupRefinementRequestMode, MakeupRefinementCapabilityReport>();

function userAgent(): string {
  return typeof navigator === 'undefined' ? '' : navigator.userAgent;
}

function detectPlatform(): MakeupRefinementPlatform {
  if (cachedPlatform) return cachedPlatform;
  const ua = userAgent();
  cachedPlatform =
    /iPad|iPhone|iPod/.test(ua) ? 'ios' :
    /Android/.test(ua) ? 'android' :
    ua ? 'desktop' :
    'unknown';
  return cachedPlatform;
}

function supportsWebGl2(): boolean {
  if (cachedWebGl2Available !== null) return cachedWebGl2Available;
  try {
    const canvas = document.createElement('canvas');
    cachedWebGl2Available = !!canvas.getContext('webgl2');
    return cachedWebGl2Available;
  } catch {
    cachedWebGl2Available = false;
    return false;
  }
}

function hasNativeRefinementBridge(): boolean {
  return typeof window !== 'undefined' && 'BeautyIQNativeRefinement' in window;
}

export function detectMakeupRefinementCapabilities(
  requestedMode: MakeupRefinementRequestMode = 'auto'
): MakeupRefinementCapabilityReport {
  const cached = capabilityCache.get(requestedMode);
  if (cached) return cached;
  const webgpuAvailable = typeof navigator !== 'undefined' && 'gpu' in navigator;
  const nativeBridgeAvailable = hasNativeRefinementBridge();
  const mode: MakeupRefinementMode = requestedMode === 'none' ? 'none' : 'local-pixel';
  const runtime: MakeupRefinementRuntime = mode === 'none' ? 'none' : 'canvas2d';

  const report = {
    platform: detectPlatform(),
    selectedMode: mode,
    runtime,
    webgpuAvailable,
    webgl2Available: supportsWebGl2(),
    offscreenCanvasAvailable: typeof OffscreenCanvas !== 'undefined',
    wasmAvailable: typeof WebAssembly !== 'undefined',
    nativeBridgeAvailable,
    reason: requestedMode === 'none'
      ? 'refinement disabled by request'
      : nativeBridgeAvailable
        ? 'native bridge detected but local-pixel remains the active safe provider'
        : 'safe cross-platform local-pixel provider selected'
  };
  capabilityCache.set(requestedMode, report);
  return report;
}

function createCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function clampByte(value: number): number {
  return value < 0 ? 0 : value > 255 ? 255 : value;
}

function maskData(mask: HTMLCanvasElement): Uint8ClampedArray | null {
  const ctx = mask.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  return ctx.getImageData(0, 0, mask.width, mask.height).data;
}

function scaledMask(mask: HTMLCanvasElement, width: number, height: number): HTMLCanvasElement {
  if (mask.width === width && mask.height === height) return mask;
  const out = createCanvas(width, height);
  const ctx = out.getContext('2d');
  if (ctx) ctx.drawImage(mask, 0, 0, width, height);
  return out;
}

function buildSemanticMasks(
  semanticMap: SemanticFaceMap,
  width: number,
  height: number
): {
  face: HTMLCanvasElement;
  lips: HTMLCanvasElement;
  eyes: HTMLCanvasElement;
} {
  const skin = scaledMask(semanticMap.masks.skin, width, height);
  const cheeks = scaledMask(combineMasks(semanticMap.width, semanticMap.height, [
    semanticMap.masks.leftCheek,
    semanticMap.masks.rightCheek
  ]), width, height);
  const eyes = scaledMask(combineMasks(semanticMap.width, semanticMap.height, [
    semanticMap.masks.leftMobileLid,
    semanticMap.masks.rightMobileLid,
    semanticMap.masks.leftCreaseFade,
    semanticMap.masks.rightCreaseFade,
    semanticMap.masks.leftLashLine,
    semanticMap.masks.rightLashLine
  ]), width, height);
  const lips = scaledMask(semanticMap.masks.lips, width, height);
  const face = softenMask(combineMasks(width, height, [skin, cheeks, eyes, lips]), Math.max(3, Math.min(width, height) * 0.006));
  return { face, lips, eyes };
}

function buildLandmarkFaceMask(
  detection: LipDetection,
  width: number,
  height: number
): HTMLCanvasElement {
  const FACE_OVAL = [
    10, 338, 297, 332, 284, 251, 389, 356, 454, 323,
    361, 288, 397, 365, 379, 378, 400, 377, 152, 148,
    176, 149, 150, 136, 172, 58, 132, 93, 234, 127,
    162, 21, 54, 103, 67, 109
  ];

  const mask = createCanvas(width, height);
  const ctx = mask.getContext('2d');
  if (!ctx) return mask;
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  let started = false;
  for (const idx of FACE_OVAL) {
    const p = detection.landmarks[idx];
    if (!p) continue;
    const x = p.x * width;
    const y = p.y * height;
    if (!started) {
      ctx.moveTo(x, y);
      started = true;
    } else {
      ctx.lineTo(x, y);
    }
  }
  if (started) ctx.closePath();
  ctx.fill();
  return softenMask(mask, Math.max(3, Math.min(width, height) * 0.008));
}

export function refineMakeupComposite(
  original: HTMLCanvasElement | ImageBitmap,
  rendered: HTMLCanvasElement,
  detection: LipDetection,
  options: MakeupRefinementOptions = {}
): MakeupRefinementResult {
  const capabilities = detectMakeupRefinementCapabilities(options.mode ?? 'auto');
  const mode = capabilities.selectedMode;
  if (mode === 'none') {
    return {
      canvas: rendered,
      mode: 'none',
      runtime: 'none',
      capabilities,
      textureStrength: 0,
      edgeBlendStrength: 0
    };
  }

  const width = rendered.width;
  const height = rendered.height;
  const textureStrength = Math.max(0, Math.min(0.32, options.textureStrength ?? 0.16));
  const edgeBlendStrength = Math.max(0, Math.min(0.45, options.edgeBlendStrength ?? 0.18));

  const originalCanvas = createCanvas(width, height);
  const originalCtx = originalCanvas.getContext('2d', { willReadFrequently: true });
  const renderedCtx = rendered.getContext('2d', { willReadFrequently: true });
  if (!originalCtx || !renderedCtx) {
    return { canvas: rendered, mode, runtime: capabilities.runtime, capabilities, textureStrength, edgeBlendStrength };
  }
  originalCtx.drawImage(original, 0, 0, width, height);

  const blurredOriginal = createCanvas(width, height);
  const blurCtx = blurredOriginal.getContext('2d', { willReadFrequently: true });
  if (!blurCtx) {
    return { canvas: rendered, mode, runtime: capabilities.runtime, capabilities, textureStrength, edgeBlendStrength };
  }
  blurCtx.drawImage(originalCanvas, 0, 0);
  stackBlur(blurCtx, 0, 0, width, height, Math.max(2, Math.min(width, height) * 0.004));

  const masks = options.semanticMap
    ? buildSemanticMasks(options.semanticMap, width, height)
    : {
        face: buildLandmarkFaceMask(detection, width, height),
        lips: createCanvas(width, height),
        eyes: createCanvas(width, height)
      };

  const faceData = maskData(masks.face);
  const lipsData = maskData(masks.lips);
  const eyesData = maskData(masks.eyes);
  if (!faceData || !lipsData || !eyesData) {
    return { canvas: rendered, mode, runtime: capabilities.runtime, capabilities, textureStrength, edgeBlendStrength };
  }

  const originalImg = originalCtx.getImageData(0, 0, width, height);
  const blurImg = blurCtx.getImageData(0, 0, width, height);
  const renderedImg = renderedCtx.getImageData(0, 0, width, height);
  const o = originalImg.data;
  const b = blurImg.data;
  const r = renderedImg.data;

  for (let i = 0; i < r.length; i += 4) {
    const faceAlpha = (faceData[i + 3] ?? faceData[i] ?? 0) / 255;
    if (faceAlpha <= 0.01) continue;

    const lipAlpha = (lipsData[i + 3] ?? lipsData[i] ?? 0) / 255;
    const eyeAlpha = (eyesData[i + 3] ?? eyesData[i] ?? 0) / 255;
    const detailScale = textureStrength
      * faceAlpha
      * (1 - 0.42 * lipAlpha)
      * (1 - 0.18 * eyeAlpha);
    const edgeScale = edgeBlendStrength * Math.max(0, 1 - Math.min(1, faceAlpha * 1.6));

    for (let c = 0; c < 3; c++) {
      const originalValue = o[i + c] ?? 0;
      const renderedValue = r[i + c] ?? 0;
      const highDetail = originalValue - (b[i + c] ?? originalValue);
      const textured = renderedValue + highDetail * detailScale;
      r[i + c] = clampByte(textured * (1 - edgeScale) + originalValue * edgeScale);
    }
  }

  const out = createCanvas(width, height);
  const outCtx = out.getContext('2d');
  if (!outCtx) return { canvas: rendered, mode, runtime: capabilities.runtime, capabilities, textureStrength, edgeBlendStrength };
  outCtx.putImageData(renderedImg, 0, 0);
  out.setAttribute('data-refinement', mode);
  out.setAttribute('data-refinement-runtime', capabilities.runtime);
  out.setAttribute('data-refinement-platform', capabilities.platform);

  return {
    canvas: out,
    mode,
    runtime: capabilities.runtime,
    capabilities,
    textureStrength,
    edgeBlendStrength
  };
}
